import { useQuery } from '@tanstack/react-query';
import { Glyph, Button, Card, EmptyState, Label, StatusDot, Thumb, Title } from '@raro/ui';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PROJECT_STATUS_LABELS, PROJECT_TYPE_LABELS, formatDate, stageLabel } from '../lib/format';
import type { ProjectListItem } from '../lib/types';

export function ProjetosPage() {
  const { me } = useAuth();
  const projects = useQuery({ queryKey: ['projects'], queryFn: () => api.get<ProjectListItem[]>('/projects') });

  const grouped = new Map<string, { name: string; items: ProjectListItem[] }>();
  for (const p of projects.data ?? []) {
    const g = grouped.get(p.organization.id) ?? { name: p.organization.name, items: [] };
    g.items.push(p);
    grouped.set(p.organization.id, g);
  }

  return (
    <div className="rr-page rr-stack rr-stack--7">
      <header className="rr-row rr-row--between">
        <div className="rr-stack rr-stack--2">
          <Glyph name="projetos" size={64} className="rr-page-glyph" /><Label>Projetos</Label>
          <Title>cada projeto é uma investigação.</Title>
        </div>
        {me?.internalRole && (
          <div className="rr-row">
            <Link to="/organizacoes/nova">
              <Button variant="quiet">novo cliente</Button>
            </Link>
            <Link to="/projetos/novo">
              <Button variant="primary">novo projeto</Button>
            </Link>
          </div>
        )}
      </header>

      {projects.isSuccess && grouped.size === 0 && (
        <EmptyState title="nenhum projeto ainda.">crie um cliente e depois um VER.</EmptyState>
      )}

      {[...grouped.entries()].map(([orgId, g]) => (
        <section key={orgId} className="rr-section">
          <Label tone="strong">{g.name}</Label>
          <div className="rr-stack rr-stack--2">
            {g.items.map((p) => (
              <Link key={p.id} to={`/projetos/${p.id}`}>
                <Card interactive className="rr-row rr-row--between">
                  <div className="rr-row" style={{ gap: 'var(--rr-space-4)' }}>
                    <Thumb src={p.cover?.thumbUrl ?? p.cover?.url} size={64} ratio="4 / 3" />
                    <StatusDot tone={p.status === 'active' ? 'active' : p.status === 'closed' ? 'idle' : 'hollow'} />
                    <div>
                      <div style={{ fontWeight: 500 }}>{p.name}{p.brand && <span className="rr-faint" style={{ fontWeight: 400, fontSize: 'var(--rr-text-xs)', marginLeft: 8 }}>{p.brand.name}</span>}</div>
                      <div className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>
                        {p.currentQuestion ?? 'sem pergunta definida'}
                      </div>
                    </div>
                  </div>
                  <div className="rr-row" style={{ gap: 'var(--rr-space-5)', fontSize: 'var(--rr-text-xs)' }}>
                    <span className="rr-label">{PROJECT_TYPE_LABELS[p.type]}</span>
                    <span className="rr-faint">{p.activeCycle ? `${p.activeCycle.name} · ${stageLabel(p.activeCycle.stage)}` : '—'}</span>
                    <span className="rr-faint">{PROJECT_STATUS_LABELS[p.status]}</span>
                    <span className="rr-faint rr-num">{formatDate(p.startedAt ?? p.createdAt)}</span>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
